import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { MESSAGES } from '../../../constants';
import { logConnectionLostMessagePresented } from '../../bi/loggers/logConnectionLostMessagePresented';
import { useIsAiExperience } from '../../common/hooks/useIsAiExperience';
import { TRootState } from '../../common/reducers';
import { errorsSlice } from '../../common/reducers/errors';
import { fieldSlice } from '../../common/reducers/field';
import { selectLastItem } from '../../common/selectors/chat';
import { selectErrors } from '../../common/selectors/errors';

export const useOfflineListener = () => {
  const dispatch = useDispatch();
  const store = useStore<TRootState>();
  const lastItem = useSelector(selectLastItem);
  const errors = useSelector(selectErrors);
  const isAiExperience = useIsAiExperience();
  const isOnlineRef = useRef(true);

  useEffect(() => {
    const localKey = 'you_are_offline_error_message';

    const goOffline = () => {
      if (
        !isAiExperience ||
        // Skip throwing offline error if we're waiting for the server to respond
        lastItem?.type === MESSAGES.BOT_TYPING ||
        // Skip throwing offline error if we already have errors
        errors.length > 0
      ) {
        return;
      }

      dispatch(errorsSlice.actions.addError({ localKey, severity: 'warning', isClosable: false }));
      dispatch(fieldSlice.actions.setDisabled(true));
      isOnlineRef.current = false;
    };

    const goOnline = () => {
      if (!isOnlineRef.current) {
        dispatch(errorsSlice.actions.removeErrorByLocalKey(localKey));
        dispatch(fieldSlice.actions.setDisabled(false));
        // We can track that connection was lost only after we're back online
        const state = store.getState();
        logConnectionLostMessagePresented(state);
        isOnlineRef.current = true;
      }
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [dispatch, lastItem, errors, store, isAiExperience]);
};
